/**
 * NewsCard - reusable card matching Session C-B Addendum §3 spec.
 *
 * One anatomy across all three tiers (My Players / My Leagues / NFL):
 *   ┌──────────┬───────────────────────────────┐
 *   │ THUMB    │ HEADLINE (2 lines max)         │
 *   │ 16:9     │ [logo] source_name · 2h ago    │
 *   │          │ [sentiment badge]              │
 *   └──────────┴───────────────────────────────┘
 *
 * Click behavior: source_url present → opens in new tab (target=_blank,
 * rel=noopener noreferrer). Null → onInternalClick callback fires
 * (parent decides where to navigate; My Leagues cards typically expand
 * or push to the league; Sleeper items open the player analytics).
 *
 * Backend contract (from GET /api/news/*): id, source_type, source_id,
 * player_id, league_id, nfl_team, headline, body, sentiment,
 * fantasy_impact, relevance_score, verified, published_at, ingested_at,
 * thumbnail_url, source_name, source_logo_url, source_url.
 */
import { ExternalLink } from "lucide-react";
import { PlayerHeadshot } from "../PlayerHeadshot";

const SENTIMENT_STYLE = {
  positive: "bg-[#00C853]/15 border-[#00C853]/40 text-[#00C853]",
  negative: "bg-[#FF6B00]/15 border-[#FF6B00]/40 text-[#FF6B00]",
};

function relativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.max(0, Math.floor((now - then) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function NewsCard({ item, onInternalClick, onPlayerClick }) {
  const external = !!item.source_url;
  const CardTag = external ? "a" : "button";
  const cardProps = external
    ? { href: item.source_url, target: "_blank", rel: "noopener noreferrer" }
    : { type: "button", onClick: () => onInternalClick?.(item) };

  const sentimentClass = SENTIMENT_STYLE[item.sentiment] || null;

  return (
    <CardTag
      {...cardProps}
      className="group flex flex-col w-[288px] shrink-0 rounded-xl bg-[#1C1C1C] border border-[#2A2A2A] overflow-hidden text-left hover:border-[#FF6B00]/60 transition-colors"
      data-testid={`news-card-${item.id}`}
    >
      {/* Thumbnail - 16:9 */}
      <div className="relative w-full aspect-[16/9] bg-[#111111] flex items-center justify-center overflow-hidden">
        {item.thumbnail_url ? (
          <img
            src={item.thumbnail_url}
            alt=""
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : item.photo_url ? (
          // [Analytics Fixes Part A A2] Real player photo from Sleeper CDN
          // (thumbnail variant) - resolved via provider_player_map by
          // backend `_hydrate_news_photos`. Founder decision (2026-07)
          // reverses the earlier silhouette-only policy for news cards.
          <img
            src={item.photo_url}
            alt=""
            data-testid={`news-photo-${item.id}`}
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : item.source_logo_url ? (
          <img
            src={item.source_logo_url}
            alt={item.source_name || ""}
            className="max-w-[50%] max-h-[50%] opacity-70"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : item.player_id ? (
          // Last resort for player-tagged items with no CDN photo - the
          // silhouette variant of PlayerHeadshot (draft-room parity).
          <div className="scale-125">
            <PlayerHeadshot playerId={item.player_id} size="md" />
          </div>
        ) : (
          // Stylized PGF lockup - final fallback.
          <div className="flex flex-col items-center gap-1.5">
            <div className="w-10 h-10 rounded-lg bg-[#FF6B00] flex items-center justify-center">
              <span className="font-display font-black text-xs text-black">PGF</span>
            </div>
            <div className="text-[9px] uppercase tracking-widest text-[#606060] font-display font-bold">
              {item.source_name || "Recap"}
            </div>
          </div>
        )}
        {external && (
          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <ExternalLink size={12} className="text-white/70" />
          </div>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 p-3 flex flex-col gap-1.5">
        <div className="font-display font-bold text-xs leading-tight text-[#F5F5F5] line-clamp-2">
          {item.headline}
        </div>
        <div className="flex items-center gap-1.5 text-[10px] text-[#A0A0A0]">
          {item.source_logo_url && (
            <img
              src={item.source_logo_url}
              alt=""
              className="w-3 h-3 rounded-sm"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          )}
          <span className="font-display font-bold uppercase tracking-wider">
            {item.source_name || "PGF"}
          </span>
          <span className="text-[#404040]">·</span>
          <span>{relativeTime(item.published_at || item.ingested_at)}</span>
        </div>
        <div className="flex items-center gap-1.5 mt-0.5">
          {sentimentClass && (
            <span
              className={`inline-flex items-center px-1.5 py-0.5 rounded-md border text-[9px] uppercase tracking-widest font-display font-black ${sentimentClass}`}
              data-testid={`news-sentiment-${item.sentiment}`}
            >
              {item.sentiment}
            </span>
          )}
          {item.player_id && item.nfl_team && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onPlayerClick?.(item.player_id);
              }}
              className="text-[9px] uppercase tracking-widest text-[#FF6B00] font-display font-bold hover:underline"
              data-testid={`news-player-tag-${item.player_id}`}
            >
              {item.nfl_team}
            </button>
          )}
        </div>
      </div>
    </CardTag>
  );
}
